import { useState, useRef, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useWebSocketNotifications } from "../hooks/useWebSocket";
import { Bell, X, Wifi, WifiOff, Car, CheckCheck } from "lucide-react";
import api from "../lib/api";

/**
 * NotificationBell
 * ----------------
 * Renders in the header for every role. Behavior:
 *   • WebSocket-only (transient) notifications for admin/driver (unchanged).
 *   • For passengers, we also fetch persistent server notifications
 *     (`GET /me/notifications`) so the "chauffeur en route" push received in
 *     the background still shows in the bell dropdown next session.
 *   • High-visibility toast + chime + vibration when a `driver_on_my_way` event
 *     lands (see TOAST_TYPES + useWebSocketNotifications).
 */
function iconForType(type) {
  if (type === "driver_on_my_way") return Car;
  return Bell;
}

function formatTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

function NotificationItem({ notification, onDismiss, onOpen }) {
  const Icon = iconForType(notification.type);
  const isOnMyWay = notification.type === "driver_on_my_way";
  return (
    <div
      className={`px-4 py-3 hover:bg-[#F8FAFC] transition-colors cursor-pointer ${!notification.read ? "bg-[#FACC15]/10" : ""}`}
      data-testid={`notification-${notification.id}`}
      onClick={() => onOpen?.(notification)}
    >
      <div className="flex items-start gap-2">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${isOnMyWay ? "bg-[#10B981]/15 text-[#10B981]" : "bg-[#0A2540]/10 text-[#0A2540]"}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          {notification.title && (
            <p className="text-xs font-bold text-[#0A2540] truncate">{notification.title}</p>
          )}
          <p className="text-xs text-[#0A2540]">{notification.body || notification.message}</p>
          <p className="text-[10px] text-[#64748B] mt-0.5">
            {isOnMyWay ? "Le chauffeur est en route" : (notification.type || "")}
            {" · "}
            {formatTime(notification.created_at || notification.time)}
          </p>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onDismiss(notification.id); }}
          className="text-[#64748B] hover:text-[#EF4444] shrink-0 p-0.5"
          aria-label="Fermer"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

function NotificationPanel({ notifications, connected, onClose, onDismiss, onOpen, onMarkAllRead }) {
  return (
    <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl border border-[#CBD5E1] shadow-xl z-50 overflow-hidden" data-testid="notification-panel">
      <div className="bg-[#0A2540] px-4 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bell className="w-4 h-4 text-[#FACC15]" />
          <span className="text-sm font-semibold text-white" style={{ fontFamily: 'Outfit' }}>Notifications</span>
          {connected ? <Wifi className="w-3 h-3 text-[#10B981]" /> : <WifiOff className="w-3 h-3 text-[#EF4444]" />}
        </div>
        <div className="flex items-center gap-2">
          {notifications.some(n => !n.read) && (
            <button onClick={onMarkAllRead} className="text-white/70 hover:text-white flex items-center gap-1 text-[10px]" data-testid="notification-mark-all-read">
              <CheckCheck className="w-3 h-3" /> Tout lu
            </button>
          )}
          <button onClick={onClose} className="text-white/60 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
      </div>
      <div className="max-h-96 overflow-y-auto divide-y divide-[#CBD5E1]/50">
        {notifications.length === 0
          ? <p className="text-center py-8 text-sm text-[#64748B]">Aucune notification</p>
          : notifications.map((n) => (
              <NotificationItem key={n.id} notification={n} onDismiss={onDismiss} onOpen={onOpen} />
            ))
        }
      </div>
    </div>
  );
}

export default function NotificationBell() {
  const { user } = useAuth();
  const {
    notifications: liveNotifs, unreadCount: liveUnread, connected,
    markAllRead: markAllLiveRead, dismiss: dismissLive,
  } = useWebSocketNotifications(user?.id);
  const [open, setOpen] = useState(false);
  const [persistent, setPersistent] = useState([]);
  const [persistentUnread, setPersistentUnread] = useState(0);
  const audioRef = useRef(null);
  const prevCountRef = useRef(0);

  const isPassenger = user?.role === "passenger";

  // Persistent fetch for passengers — merges backend "driver_on_my_way" etc.
  // notifications so the bell survives page reloads.
  const refreshPersistent = useCallback(async () => {
    if (!isPassenger || !user?.id) return;
    try {
      const { data } = await api.get("/me/notifications", { params: { limit: 30 } });
      setPersistent(data.items || []);
      setPersistentUnread(data.unread || 0);
    } catch (_e) { /* silent */ }
  }, [isPassenger, user?.id]);

  useEffect(() => { refreshPersistent(); }, [refreshPersistent]);

  // Refresh whenever a live WS notification arrives (server also persisted it)
  useEffect(() => {
    const handler = (evt) => {
      const t = evt?.detail?.type;
      if (isPassenger && (t === "driver_on_my_way" || t === "pack_purchase_approved" || t === "welcome_promo")) {
        refreshPersistent();
      }
    };
    window.addEventListener("trigo:ws-message", handler);
    return () => window.removeEventListener("trigo:ws-message", handler);
  }, [isPassenger, refreshPersistent]);

  // Combined view: live (in-memory) first, then persistent (server-side).
  // Live notifs use ephemeral ids (timestamps). We tag persistent ones with
  // an object id to distinguish. Duplicates on booking_id are OK since one
  // is toast-only and the other is history.
  const combined = isPassenger
    ? [
        ...liveNotifs.map(n => ({
          id: n.id,
          type: n.type,
          title: n.title,
          body: n.message,
          read: n.read,
          created_at: n.time,
          booking_id: n.booking_id,
          _live: true,
        })),
        ...persistent.filter(p => !liveNotifs.some(l => l.booking_id && l.booking_id === p.booking_id && l.type === p.type)),
      ]
    : liveNotifs;

  const unreadCount = liveUnread + (isPassenger ? persistentUnread : 0);

  const dismiss = useCallback(async (id) => {
    // Live notifs: pop from memory. Persistent: mark read on server.
    const isLive = liveNotifs.some(n => n.id === id);
    if (isLive) { dismissLive(id); return; }
    try {
      await api.post(`/me/notifications/${id}/read`);
    } catch (_e) { /* silent */ }
    setPersistent(prev => prev.filter(n => n.id !== id));
    setPersistentUnread(u => Math.max(0, u - 1));
  }, [liveNotifs, dismissLive]);

  const markAllRead = useCallback(async () => {
    markAllLiveRead();
    if (isPassenger) {
      try { await api.post("/me/notifications/read-all"); } catch (_e) { /* silent */ }
      setPersistent(prev => prev.map(n => ({ ...n, read: true })));
      setPersistentUnread(0);
    }
  }, [isPassenger, markAllLiveRead]);

  const openItem = useCallback((n) => {
    if (n.booking_id) {
      window.location.href = `/passenger?tab=bookings`;
    }
  }, []);

  // Local ping: play a subtle chime when the total unread count grows.
  useEffect(() => {
    if (unreadCount > prevCountRef.current && unreadCount > 0) {
      try {
        if (!audioRef.current) {
          audioRef.current = new Audio("data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1rZ2loZmRlZ2hqa3F3e4CBg4iMjpGWmp+kp6uvsrW5vb/CxMXGx8bFw8C8uLSvqqSemJKMhoF7dnFtamhlZGNjZWdqbnJ3fIGGi5CWm6Cjp6qssLS3uru8vb6+vry6t7SxraqmoZ2YlI+LhoJ+enZ0cnFwcHJ0d3qAhImPk5ianqKmqa2ws7W3ubi5ubi2tLGurKmmoZ2ZlZGNiYWCf3x6eXh4eXt+gYWJjZKWmZ2goaKjo6OjpKSjo6KhoJ2bmJWSj42LiIaEgoB/fn5/gIKFh4uOkZOVlpaXl5eYmJiYmJeXlpWUk5GQjo2LioiHhoWEhISDhIWGh4mLjI6QkZKTlJSUlZWVlJSUk5KRkI+OjYyLioqJiIiIh4eHh4iJiouMjY6Pj5CQkJGRkZGRkZCQj4+OjY2MjIuLi4qKioqKiomKioqLi4yMjI2NjY6Ojo6Ojo6Ojo6OjY2NjIyMi4uL");
        }
        audioRef.current.play().catch(() => {});
      } catch (e) {
        if (process.env.NODE_ENV === "development") console.warn("Audio playback not available:", e.message);
      }
    }
    prevCountRef.current = unreadCount;
  }, [unreadCount]);

  if (!user) return null;

  return (
    <div className="relative" data-testid="notification-bell">
      <button
        onClick={() => { setOpen(!open); if (!open && unreadCount > 0) markAllRead(); }}
        className="relative p-2 text-white/80 hover:text-white transition-colors"
        data-testid="notification-bell-btn"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[20px] h-5 px-1 bg-[#EF4444] text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse" data-testid="notification-badge">
            {unreadCount}
          </span>
        )}
      </button>

      {open && (
        <NotificationPanel
          notifications={combined}
          connected={connected}
          onClose={() => setOpen(false)}
          onDismiss={dismiss}
          onOpen={openItem}
          onMarkAllRead={markAllRead}
        />
      )}
    </div>
  );
}
